// src/pages/CustApiMappings.jsx
import React from "react";
import ConfirmModal from "../components/ConfirmModal";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const API_PATH = "/cust-api-mappings";
const PROVIDER_SUGGESTIONS = ["WAYLI", "ILS"];

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const ensureIds = (rows = []) => rows.map((row) => (row && row._id ? row : { _id: genId(), ...row }));

export default function CustApiMappings() {
  const [provider, setProvider] = React.useState("WAYLI");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const normalizedProvider = React.useMemo(() => (provider || "").toUpperCase().trim(), [provider]);

  const fetchRows = React.useCallback(async () => {
    if (!normalizedProvider) {
      setRows([]);
      setError("Provider is required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const me = getCurrentUser();
      const headers = {};
      if (me) {
        headers["X-User-Email"] = me.email;
        headers["X-User-Role"] = me.role || "";
      }
      const url = new URL(`${API_BASE}${API_PATH}`);
      url.searchParams.set("provider", normalizedProvider);
      const response = await fetch(url.toString(), { headers });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Failed to load mappings.");
      }
      setRows(ensureIds(payload.rows || []));
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load mappings.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [normalizedProvider]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const onAddRow = () => {
    setRows((prev) => [
      {
        _id: genId(),
        provider_customer_abbr: "",
        customer_abbreviation_name: "",
        cust_code: "",
        remark: "",
      },
      ...prev,
    ]);
  };

  const toggleRow = (rowId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const askDelete = () => {
    if (selectedIds.size === 0) return;
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setRows((prev) => prev.filter((row) => !selectedIds.has(row._id)));
    setSelectedIds(new Set());
    setShowDeleteModal(false);
  };

  const onCellChange = (idx, key, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const duplicateAbbrSet = React.useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const key = (row.provider_customer_abbr || "").trim().toUpperCase();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [rows]);

  const onSave = async () => {
    setError("");
    if (!normalizedProvider) {
      setError("Provider is required.");
      return;
    }
    for (const row of rows) {
      if (!(row.provider_customer_abbr || "").trim()) {
        setError("Provider Abbreviation is required.");
        return;
      }
      if (!(row.cust_code || "").trim()) {
        setError("Cust Code is required.");
        return;
      }
    }
    if (duplicateAbbrSet.size > 0) {
      setError("Duplicate Provider Abbreviation is not allowed.");
      return;
    }
    setLoading(true);
    try {
      const me = getCurrentUser();
      const headers = { "Content-Type": "application/json" };
      if (me) {
        headers["X-User-Email"] = me.email;
        headers["X-User-Role"] = me.role || "";
      }
      const mappings = rows.map((row) => ({
        provider_customer_abbr: (row.provider_customer_abbr || "").trim(),
        customer_abbreviation_name: (row.customer_abbreviation_name || "").trim(),
        cust_code: (row.cust_code || "").trim(),
        remark: (row.remark || "").trim(),
      }));
      const response = await fetch(`${API_BASE}${API_PATH}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ provider: normalizedProvider, mappings }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Save failed.");
      }
      await fetchRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const providerInputId = "cust-api-provider";

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex flex-col">
          <label htmlFor={providerInputId} className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Provider
          </label>
          <input
            id={providerInputId}
            type="text"
            list="cust-api-provider-suggestions"
            value={provider}
            onChange={(e) => setProvider((e.target.value || "").toUpperCase())}
            className="mt-1 rounded border px-3 py-2 text-sm"
            placeholder="e.g. WAYLI"
          />
          <datalist id="cust-api-provider-suggestions">
            {PROVIDER_SUGGESTIONS.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchRows}
            className="px-3 py-2 rounded border bg-gray-50 text-sm"
            disabled={loading || !normalizedProvider}
          >
            {loading ? "Loading..." : "Reload"}
          </button>
          <button
            type="button"
            onClick={onAddRow}
            className="px-3 py-2 rounded border bg-gray-50 text-sm"
            disabled={loading}
          >
            + Add Row
          </button>
          <button
            type="button"
            onClick={askDelete}
            className={`px-3 py-2 rounded text-sm ${
              selectedIds.size === 0 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-red-600 text-white"
            }`}
            disabled={selectedIds.size === 0}
          >
            Delete
          </button>
          <button
            type="button"
            onClick={onSave}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
            disabled={loading || !normalizedProvider}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="rounded border overflow-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th style={{ width: 40 }} className="border-b px-2 py-2 text-left text-xs font-semibold text-gray-500">
                &nbsp;
              </th>
              <th className="border-b px-3 py-2 text-left text-xs font-semibold text-gray-600">customer_abbreviation*</th>
              <th className="border-b px-3 py-2 text-left text-xs font-semibold text-gray-600">
                customer_abbreviation_name
              </th>
              <th className="border-b px-3 py-2 text-left text-xs font-semibold text-gray-600">Cust Code*</th>
              <th className="border-b px-3 py-2 text-left text-xs font-semibold text-gray-600">Remark</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  No mappings. Click “Add Row” to get started.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const abbrUpper = (row.provider_customer_abbr || "").trim().toUpperCase();
                const isDup = !!abbrUpper && duplicateAbbrSet.has(abbrUpper);
                return (
                  <tr key={row._id} className="border-t">
                    <td className="px-2 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row._id)}
                        onChange={(e) => toggleRow(row._id, e.target.checked)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.provider_customer_abbr || ""}
                        onChange={(e) => onCellChange(idx, "provider_customer_abbr", e.target.value)}
                        className={`w-full rounded border px-2 py-1 text-sm ${isDup ? "border-red-400 bg-red-50" : ""}`}
                        placeholder="e.g. 10003"
                      />
                      {isDup && <p className="text-xs text-red-600 mt-1">Duplicate abbreviation</p>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.customer_abbreviation_name || ""}
                        onChange={(e) => onCellChange(idx, "customer_abbreviation_name", e.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                        placeholder="Provider label"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.cust_code || ""}
                        onChange={(e) => onCellChange(idx, "cust_code", e.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                        placeholder="WLGBL"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.remark || ""}
                        onChange={(e) => onCellChange(idx, "remark", e.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                        placeholder="Notes"
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        title="Delete selected mappings?"
        message={`This will remove ${selectedIds.size} mapping(s) for ${normalizedProvider || "(blank)"}.`}
        confirmLabel="Delete"
        confirmClassName="bg-red-600 text-white"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
